import React, { useContext, useMemo, useState } from 'react';
import { Autocomplete } from '@mui/material';
import { Control, Controller, FieldError, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CheckoutContext, CheckoutContextProps } from '../../../context/CheckoutContext';
import { CompanySchool } from '../../../hooks/useCompany';
import {
  AddNewOptionRow,
  FieldContainer,
  FieldErrorText,
  FieldHintRow,
  FieldHintText,
  FieldLabel,
  NewSchoolFieldContainer,
  OptionRow,
  OptionText,
  SchoolDropdownPaper,
  SchoolTextField,
} from './schoolSelectFieldStyle';

export const ADD_NEW_SCHOOL_OPTION_ID = '__add_new_school__';

type SchoolFormFields = {
  schoolId?: string;
  schoolName?: string;
  newSchoolName?: string;
};

type SchoolOption = {
  id: string;
  name: string;
};

type SchoolSelectFieldProps = {
  control: Control<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  schoolIdError?: FieldError;
  newSchoolNameError?: FieldError;
};

const SchoolSelectField: React.FC<SchoolSelectFieldProps> = ({
  control,
  watch,
  setValue,
  schoolIdError,
  newSchoolNameError,
}) => {
  const { company } = useContext(CheckoutContext) as CheckoutContextProps;
  const schools = (company?.schools || []) as CompanySchool[];
  const selectedSchoolId = watch('schoolId');
  const [inputValue, setInputValue] = useState('');

  const options = useMemo<SchoolOption[]>(
    () => [
      ...schools.map(school => ({ id: school.id, name: school.name })),
      { id: ADD_NEW_SCHOOL_OPTION_ID, name: 'Other / Add new school…' },
    ],
    [schools]
  );

  const selectedOption =
    options.find(option => option.id === selectedSchoolId) ||
    (selectedSchoolId && selectedSchoolId !== ADD_NEW_SCHOOL_OPTION_ID
      ? { id: selectedSchoolId, name: watch('schoolName') || '' }
      : null);

  const isAddingNew = selectedSchoolId === ADD_NEW_SCHOOL_OPTION_ID;

  return (
    <FieldContainer>
      <FieldLabel htmlFor="schoolId">School*</FieldLabel>
      <Controller
        name="schoolId"
        control={control}
        render={({ field }) => (
          <Autocomplete
            options={options}
            value={selectedOption}
            inputValue={inputValue}
            onInputChange={(_, value) => setInputValue(value)}
            getOptionLabel={option => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            popupIcon={<KeyboardArrowDownRoundedIcon />}
            PaperComponent={SchoolDropdownPaper}
            noOptionsText="No matching school — choose “Other / Add new school…”"
            onChange={(_, value) => {
              field.onChange(value?.id || '');
              if (!value || value.id === ADD_NEW_SCHOOL_OPTION_ID) {
                setValue('schoolName', '');
                return;
              }
              setValue('schoolName', value.name);
              setValue('newSchoolName', '');
            }}
            renderOption={(props, option) => {
              const isAddNewOption = option.id === ADD_NEW_SCHOOL_OPTION_ID;
              const Row = isAddNewOption ? AddNewOptionRow : OptionRow;
              return (
                <li {...props} key={option.id}>
                  <Row>
                    {isAddNewOption ? <AddCircleOutlineRoundedIcon /> : <SchoolOutlinedIcon />}
                    <OptionText>{option.name}</OptionText>
                  </Row>
                </li>
              );
            }}
            renderInput={params => (
              <SchoolTextField
                {...params}
                id="schoolId"
                variant="standard"
                placeholder="Search or select your school"
                error={Boolean(schoolIdError)}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                }}
              />
            )}
          />
        )}
      />
      {schoolIdError && <FieldErrorText>{schoolIdError.message}</FieldErrorText>}

      {isAddingNew && (
        <NewSchoolFieldContainer>
          <FieldLabel htmlFor="newSchoolName">New school name*</FieldLabel>
          <Controller
            name="newSchoolName"
            control={control}
            render={({ field }) => (
              <SchoolTextField
                {...field}
                id="newSchoolName"
                variant="standard"
                placeholder="Enter school name"
                error={Boolean(newSchoolNameError)}
                InputProps={{ disableUnderline: true }}
                onChange={e => {
                  field.onChange(e);
                  setValue('schoolName', e.target.value);
                }}
              />
            )}
          />
          {newSchoolNameError && <FieldErrorText>{newSchoolNameError.message}</FieldErrorText>}
        </NewSchoolFieldContainer>
      )}

      <FieldHintRow>
        <InfoOutlinedIcon />
        <FieldHintText>
          Cannot find your school? Choose “Other / Add new school…” — it will be available for
          others next time.
        </FieldHintText>
      </FieldHintRow>
    </FieldContainer>
  );
};

export type { SchoolFormFields };
export default SchoolSelectField;
